import { Address } from './config'

export interface ExternalReference {
  guid: string
  externalId?: string
  entityType?: string
}

type ApprovalStatus = 'NEEDS_APPROVAL' | 'APPROVED' | 'FUTURE' | 'NOT_APPROVED'

type PricingFeatures = 'TAXESV2' | 'TAXESV3'

type CheckPaymentStatus = 'OPEN' | 'PAID' | 'CLOSED'

type RefundStatus = 'NONE' | 'FULL' | 'PARTIAL'

export type UnitOfMeasure = 'NONE' | 'LB' | 'OZ' | 'KG' | 'G'

export type CartPaymentType = 'CREDIT' | 'OTHER' | 'ZERO_DOLLAR_ORDER'

export type CheckPaymentType =
  | 'CASH'
  | 'CREDIT'
  | 'GIFTCARD'
  | 'HOUSE_ACCOUNT'
  | 'REWARDCARD'
  | 'LEVELUP'
  | 'OTHER'
  | 'UNDETERMINED'
type CardEntryMode =
  | 'SWIPED'
  | 'KEYED'
  | 'ONLINE'
  | 'EMV_CHIP_SIGN'
  | 'TOKENIZED'
  | 'PRE_AUTHED'
  | 'SAVED_CARD'
  | 'FUTURE_ORDER'
  | 'CONTACTLESS'
  | 'APPLE_PAY_CNP'
  | 'GOOGLE_PAY_CNP'
  | 'INCREMENTAL_PRE_AUTHED'
  | 'PARTNER_ECOM_COF'

export enum PaymentStatus {
  OPEN = 'OPEN',
  PROCESSING = 'PROCESSING',
  AUTHORIZED_AT_RISK = 'AUTHORIZED_AT_RISK',
  AUTHORIZED = 'AUTHORIZED',
  ERROR = 'ERROR',
  ERROR_NETWORK = 'ERROR_NETWORK',
  DENIED = 'DENIED',
  PROCESSING_VOID = 'PROCESSING_VOID',
  VOIDED_AT_RISK = 'VOIDED_AT_RISK',
  CANCELLED = 'CANCELLED',
  CAPTURE_IN_PROGRESS = 'CAPTURE_IN_PROGRESS',
  CAPTURED = 'CAPTURED',
  VOIDED = 'VOIDED'
}

type CardType =
  | 'VISA'
  | 'MASTERCARD'
  | 'AMEX'
  | 'DISCOVER'
  | 'JCB'
  | 'DINERS'
  | 'CIT'
  | 'MAESTRO'
  | 'LASER'
  | 'SOLO'
  | 'UNKNOWN'

type SelectionType =
  | 'NONE'
  | 'OPEN_ITEM'
  | 'SPECIAL_REQUEST'
  | 'PORTION'
  | 'HOUSE_ACCOUNT_PAY_BALANCE'

interface RefundDetails {
  refundAmount: number
  taxRefundAmount: number
  refundTransactions: ExternalReference
}

interface AppliedTaxRate {
  taxRate?: ExternalReference
  rate?: number
  entityType?: string
  name?: string
  guid: string
  taxAmount: number
  type: string
  facilitatorCollectAndRemitTax: boolean
}

export interface AppliedServiceCharge {
  delivery: boolean
  taxable: boolean
  serviceChargeCalculation: 'PRE_DISCOUNT' | 'POST_DISCOUNT'
  refundDetails: RefundDetails
  chargeType: 'FIXED' | 'PERCENT' | 'OPEN'
  gratuity: boolean
  takeout: boolean
  serviceCharge: ExternalReference
  name: string
  chargeAmount: number
  appliedTaxes: AppliedTaxRate
  dineIn: boolean
}

export interface Payment extends ExternalReference {
  originalProcessingFee?: number
  server?: ExternalReference
  cashDrawer?: ExternalReference
  refundStatus: RefundStatus
  type: CheckPaymentType
  voidInfo?: {
    voidUser: ExternalReference
    voidApprover: ExternalReference
    voidDate: string
    voidBusinessDate: number
    voidReason: ExternalReference
  }
  checkGuid: string
  otherPayment?: ExternalReference
  paidDate?: string
  orderGuid: string
  cardEntryMode?: CardEntryMode
  cardPaymentId?: string
  paymentStatus: PaymentStatus
  amount?: number
  tipAmount?: number
  amountTendered?: number
  cardType?: CardType
  houseAccount?: ExternalReference
  mcaRepaymentAmount?: number
  giftCard?: ExternalReference
  referenceCode?: string
  paidBusinessDate?: number
  last4Digits?: string
  refund: {
    refundAmount: number
    tipRefundAmount: number
    refundDate: string
    refundBusinessDate: number
    refundTransaction: ExternalReference
  }
}

interface MarketplaceFacilitatorTaxInfo {
  facilitatorCollectAndRemitTaxOrder: boolean
  appliedTaxRates: AppliedTaxRate[]
}

export interface Customer {
  phoneToken?: string
  firstName?: string
  lastName?: string
  emailToken?: string
  phone?: string
  phoneCountryCode?: string
  guid?: string
  email?: string
}

export interface AppliedLoyaltyInfo {
  guid: string
}

interface LoyaltyDetails {
  vendor?: string
  referenceId?: string
}

export type DiscountAmountType =
  | 'PERCENT'
  | 'FIXED'
  | 'OPEN_PERCENT'
  | 'OPEN_FIXED'
  | 'BOGO'
  | 'FIXED_TOTAL'

type DiscountProcessingState =
  | 'PENDING_APPLIED'
  | 'APPLIED'
  | 'VOID'
  | 'PENDING_VOID'

export interface AppliedDiscount {
  name: string
  guid?: string
  discount: ExternalReference
  discountType?: DiscountAmountType
  discountAmount?: number
  discountPercent?: number
  nonTaxDiscountAmount?: number
  processingState?: DiscountProcessingState
  loyaltyDetails?: LoyaltyDetails
  appliedPromoCode?: string
}

export interface Check extends ExternalReference {
  displayNumber?: string
  payments: Payment[]
  appliedDiscounts: AppliedDiscount[]
  voidDate?: string
  paidDate?: string
  voided: boolean
  paymentStatus: CheckPaymentStatus
  amount: number
  tabName: string
  taxExempt: boolean
  openedDate: string
  totalAmount: number
  tipAmount: number
  totalDiscountAmount: number
  selections: Selection[]
  voidBusinessDate?: number
  createdDate: string
  deleted: boolean
  closedDate?: string
  deletedDate?: string
  modifiedDate?: string
  taxAmount: number
  appliedServiceCharges: AppliedServiceCharge[]
  customer: Customer
  appliedLoyaltyInfo?: AppliedLoyaltyInfo
  taxExemptionAccount?: TaxExemptionAccount
}

export interface TaxExemptionAccount {
  state?: string
  number?: string
}

export interface Selection extends ExternalReference {
  deferred: boolean
  preDiscountPrice: number
  voidReasion?: ExternalReference
  optionGroup?: ExternalReference
  displayName: string
  appliedDiscounts: AppliedDiscount[]
  externalPriceAmount: number
  modifiers: Selection[]
  seatNumber?: number
  voidDate?: string
  fulfillmentStatus: 'NEW' | 'HOLD' | 'SENT' | 'READY'
  optionGroupPricingMode:
    | 'INCLUDED'
    | 'FIXED_PRICE'
    | 'ADJUSTS_PRICE'
    | 'REPLACES_PRICE'
    | 'LOCATION_SPECIFIC_RATE'
  salesCategory?: ExternalReference
  selectionType: SelectionType
  price: number
  voided: boolean
  appliedTaxes: AppliedTaxRate[]
  itemGroup: ExternalReference
  item: ExternalReference
  taxInclusion: 'INCLUDED' | 'NOT_INCLUDED' | 'INHERITED'
  quantity: number
  receiptLinePrice: number
  unitOfMeasure?: UnitOfMeasure
  refundDetails?: RefundDetails
  tax: number
  diningOption: ExternalReference
  openPriceAmount?: number
  voidBusinessDate?: number
  createdDate: string
  preModifier?: ExternalReference
  modifiedDate?: string
}

export interface Order extends ExternalReference {
  revenueCenter?: ExternalReference
  server?: ExternalReference
  source: string
  guestOrderStatus?: string
  voidDate?: string
  duration?: number
  businessDate?: number
  paidDate?: string
  restaurantService?: ExternalReference
  voided: boolean
  estimatedFulfillmentDate?: string
  table?: ExternalReference
  requiredPrepTime?: string
  approvalStatus: ApprovalStatus
  serviceArea: ExternalReference
  numberOfGuests?: number
  diningOption: ExternalReference
  deliveryInfo?: DeliveryInfo
  deliveryServiceInfo?: DeliveryServiceInfo
  marketplaceFacilitatorTaxInfo?: MarketplaceFacilitatorTaxInfo
  initialDate?: number
  openedDate?: string
  voidBusinessDate?: string
  checks: Check[]
  deleted: boolean
  createdDate?: string
  closedDate?: string
  deletedDate?: string
  modifiedDate?: string
  promisedDate?: string
  pricingFeatures: PricingFeatures[]
}

export interface CompletedOrder extends Order {
  customLocationName?: string
  customLocationAddress?: Address
  endDate?: string
}

export interface SelectionRequest {
  guid?: string
  externalId?: string
  item: ExternalReference
  itemGroup?: ExternalReference
  selectionType: SelectionType
  optionGroup?: ExternalReference
  quantity?: number
  modifiers: ModifierSelectionRequest[]
}

export interface ItemSelectionRequest extends SelectionRequest {
  fastLinkName: string | undefined
}

export interface SpecialRequestSelectionRequest {
  displayName: string
  selectionType: 'SPECIAL_REQUEST'
  quantity?: number
}

export type ModifierSelectionRequest =
  | SelectionRequest
  | SpecialRequestSelectionRequest

export interface DeliveryInfo {
  address1?: string
  address2?: string
  city?: string
  state?: string
  zipCode?: string
  latitude?: number
  longitude?: number
  notes?: string
  country?: string
}

export interface DeliveryServiceInfo {
  originalQuotedDeliveryDate?: string
  driverName?: string
  driverPhoneNumber?: string
  providerName?: 'TOAST_DELIVERY_SERVICES'
}

export enum DiscountSelectionType {
  CHECK = 'CHECK',
  ITEM = 'ITEM'
}

export interface DiscountEntity {
  active: boolean
  discountAmount?: number | null
  entityType: string
  fixedTotal: any
  guid: string
  itemPickingPriority: string
  name: string
  nonExclusive: boolean
  discountPercent?: number | null
  selectionType: DiscountSelectionType
  type: DiscountAmountType
}
